import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/lib/i18n";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";

const Login = lazy(() => import("@/pages/Login"));
const ClientLogin = lazy(() => import("@/pages/ClientLogin"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));
const ClientGallery = lazy(() => import("@/pages/ClientGallery"));
const SummaryPage = lazy(() => import("@/pages/SummaryPage"));
const ClientPortal = lazy(() => import("@/pages/ClientPortal"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const Settings = lazy(() => import("@/pages/Settings"));
const Billing = lazy(() => import("@/pages/Billing"));
const PlatformAdmin = lazy(() => import("@/pages/PlatformAdmin"));
const DevLogin = lazy(() => import("@/pages/DevLogin"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const AboutUs = lazy(() => import("@/pages/AboutUs"));
const ContactUs = lazy(() => import("@/pages/ContactUs"));

const Spinner = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#707070]">
    <Loader2 className="animate-spin" />
  </div>
);

const TrialGate = ({ children }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const staff = user?.tenant_role === "owner" || user?.tenant_role === "admin";
  if (staff && user?.trial?.expired && pathname !== "/billing") return <Navigate to="/billing" replace />;
  return children;
};

const Protected = ({ children, roles }) => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles) {
    const ok = roles.includes(user.tenant_role) || roles.includes(user.platform_role);
    if (!ok) {
      const home =
        user.platform_role === "platform_owner" ? "/platform" : user.tenant_role === "client" ? "/portal" : "/dashboard";
      // Never bounce a route to itself — an account with no usable role goes back to login.
      return <Navigate to={home === pathname ? "/login" : home} replace />;
    }
  }
  return <TrialGate>{children}</TrialGate>;
};

const STAFF = ["owner", "admin"];
const OWNER_ONLY = ["owner"];
const PLATFORM_ONLY = ["platform_owner"];
const CLIENT_ONLY = ["client"];

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<Spinner />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/client-login" element={<ClientLogin />} />
      <Route path="/console-access" element={<DevLogin />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/contact-us" element={<ContactUs />} />
      <Route path="/dashboard" element={<Protected roles={STAFF}><Dashboard /></Protected>} />
      <Route path="/projects/:projectId" element={<Protected roles={STAFF}><ProjectDetail /></Protected>} />
      <Route path="/analytics" element={<Protected roles={STAFF}><Analytics /></Protected>} />
      <Route path="/settings" element={<Protected roles={OWNER_ONLY}><Settings /></Protected>} />
      <Route path="/billing" element={<Protected roles={STAFF}><Billing /></Protected>} />
      <Route path="/platform" element={<Protected roles={PLATFORM_ONLY}><PlatformAdmin /></Protected>} />
      <Route path="/portal" element={<Protected roles={CLIENT_ONLY}><ClientPortal /></Protected>} />
      <Route path="/g/:token" element={<ClientGallery />} />
      <Route path="/g/:token/summary" element={<SummaryPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <Toaster position="top-center" theme="dark" offset={80} />
          <AppRouter />
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}
